/**
 * GameManager.js
 * Manage the game state and most of the element related to normal gameplay
 *
 * State machine
 * -------------------------------------
 * eGAME_START: The game/level begins.
 * eSTART_WAVE: A new wave begins. An enemy preemptive strike might occur before the player's turn.
 * ePLAYER_TURN: It is the player's turn to act.
 * eFIRING_BUBBLE: The player has fired a bubble and the bubble is moving.
 * eBUBBLE_CONTACT: The fired bubble has collided with something, waiting for the damage effects to complete.
 * eAPPLY_DAMAGE: It is time to apply damage to ennemies (attack particles are all done).
 * eENEMY_ATTACK: After a miss, it is the ennemies turn to act.
 * eBUBBLE_CHANGES: After turn is completed, apply changes to bubbles in the grid (abilities/chameleon/timers).
 * eBLACK_PANTHER_ATTACK: If applicable, activate Black Panther ability.
 * eVICTORY: The player has completed the level.
 * eDEFEAT: The player is defeated.
 * eDONE: The game/level is done, time to show the result screen.
 */

import { Game, GameScene, GameTimer, GameTween } from "pixi_core";

import { Shooter } from "./Shooter";
import { Grid } from "./Grid";
import { Hero } from "../Heroes/Hero";
import { Level } from "./Level";
import { BubbleManager } from "./BubbleManager";
import { BubbleFactory } from "./BubbleFactory";
import { HeroesManager } from "./HeroesManager";
import { EnemiesManager } from "./EnemiesManager";
import { BackgroundManager } from "./BackgroundManager";
import { ParticlesManager } from "./particles/ParticlesManager";
import { Bubble } from "./Bubble";
import { Common } from "./Common";
import { Lightning } from "./Lightning";
import { BlackPantherShockwave } from "./BlackPantherShockwave";
import { WallBounce } from "./WallBounce";
import { RandomSelector } from "./RandomSelector";

import { Ability } from "./abilities/Ability";
import { AbilityAddBubble } from "./abilities/AbilityAddBubble";
import { AbilityChariot } from "./abilities/AbilityChariot";
import { AbilityAttack } from "./abilities/AbilityAttack";
import { AbilityAddLine } from "./abilities/AbilityAddLine";
import { AbilityStun } from "./abilities/AbilityStun";
import { AbilityHacker } from "./abilities/AbilityHacker";
import { AbilityRandomShooter } from "./abilities/AbilityRandomShooter";
import { AbilityTransformToShield } from "./abilities/AbilityTransformToShield";
import { AbilityTransformToStone } from "./abilities/AbilityTransformToStone";

import {
    shuffle,
    BubbleColor,
    SceneLayers,
    GameState,
    BubblePool,
    Heroes,
    nOffsetOrder,
    CAMERA_TARGET_HEIGHT,
    CAMERA_MIN_WIDTH,
    FIRED_BUBBLE_COLLISION_LINE_LENGTH,
    NB_COLOR_MATCH,
    MULTICOLOR_VALUES,
    NB_MAX_COLUMN,
    NEIGHBORS_OFFSETS,
    TEMP_DELAY_POP_BURST_BUBBLE,
    TEMP_DELAY_CHANGE_CHAMELEON_COLOR,
    TEMP_BLOOMING_SPAWN_DELAY,
    LAST_LEVEL,
    BUBBLE_COLOR_NAME_TO_ID,
    CheatEvents,
    GameEvents,
    TEAM_SIZE,
    Tutorials,
    TutorialStep,
    FIRST_LEVEL,
    LEVEL_ABILITIES,
    BubblePopSource,
    BuffDebuff,
    SHOOTER_X_POSITION,
    SHOOTER_Y_POSITION,
    ENEMY_ABILITY_PARTICLE_TRAVEL_TIME
} from "./../Constants";

export class GameManager {
    /**
     * @public
     */
    constructor() {
        /** @private @type {import("../Constants").AnimationSettings} */
        this._animationSettings = Common.settings.animations;

        /** @private @type {GameState} */
        this._state = GameState.eUNDEFINED;

        /** @private @type {Hero[]} */
        this._party = [];

        /** @private @type {Number} */
        this._levelId = 0;

        /** @private @type {Object[]} */
        this._levelWaves = [];

        /** @private @type {GameScene} */
        this._scene = Game.scenes.createScene("Level");
        Common.scene = this._scene;

        this._scene.createLayer(SceneLayers.eBACKGROUND);
        this._scene.createLayer(SceneLayers.eWALLS);
        this._scene.createLayer(SceneLayers.eAIM_LINE, { overUi: true });
        this._scene.createLayer(SceneLayers.eBUBBLES, { overUi: true });
        this._scene.createLayer(SceneLayers.eBUBBLES_DROPPED, { overUi: true });
        this._scene.createLayer(SceneLayers.ePARTICLES, { overUi: true });
        this._scene.createLayer(SceneLayers.eBUBBLE_OVER, { overUi: true });
        this._scene.createLayer(SceneLayers.eBUBBLE_FIRED, { overUi: true });
        this._scene.createLayer(SceneLayers.eOVER_BUBBLES_UI, { overUi: true });
        this._scene.setDefaultLayer(SceneLayers.eBUBBLES);
        this._scene.active = false;

        this._scene.camera.orthoHeight = CAMERA_TARGET_HEIGHT / 2;
        this._scene.camera.minOrthoWidth = CAMERA_MIN_WIDTH / 2;

        /** @private @type {Level} */
        this._currentLevel = null;

        /** @private @type {Grid} */
        this._grid = new Grid({ x: 0, y: 0 });

        /** @private @type {Bubble[]} */
        this._removedBubbles = [];

        /** @private @type {BackgroundManager} */
        this._backgroundManager = null;

        /** @private @type {ParticlesManager} */
        this._particlesManager = null;

        /** @private @type {BubbleManager} */
        this._bubbleManager = new BubbleManager(this._grid);

        /** @private @type {BubbleFactory} */
        this._bubbleFactory = new BubbleFactory(this._grid, this._scene, this._bubbleManager);

        /** @private @type {Shooter} */
        this._shooter = null;

        /** @private @type {BlackPantherShockwave} */
        this._blackPantherShockwave = null;

        /** @private @type {WallBounce} */
        this._wallBounce = null;

        /** @private @type {GameTimer[]} */
        this._bloomingTimer = [];

        /** @private @type {Lightning} - Chain lightning effect */
        this._lightning = new Lightning(this._scene);

        /** @private @type {HeroesManager} */
        this._heroes = new HeroesManager();

        /** @private @type {EnemiesManager} */
        this._enemies = new EnemiesManager();

        /** @private @type {Ability[]} */
        this._performedAbilities = [];

        /** @private @type {BubbleColor} */
        this._lastShotColor = BubbleColor.eRANDOM_EDITOR;
        /** @private @type {Bubble} */
        this._firedBubble = null;
        /** @private @type {Number} */
        this._firedAngle = 0;
        /** @private @type {Number} */
        this._currentWave = 0;
        /** @private @type {Number} */
        this._bubbleContactStateTime = 0;

        /** @private @type {Boolean} - Used to track a special bubble hit/miss */
        this._specialBubbleHit = false;

        /** @private @type {GameState} */
        this._gameState = GameState.eUNDEFINED;

        /** @private @type {Boolean} */
        this._preemptiveStrike = false;

        /** @private @type {import("../Constants").CollisionData} - re-usable result data */
        this._collisionResult = {
            hit: false,
            hitBubble: false,
            hitWall: false,
            hitSideWall: false,
            hitBackWall: false,
            bubble: null,
            contactX: 0,
            contactY: 0
        };

        /** @private @type {GameTween} */
        this._tweenBubbleMovement = null;

        /** @private @type {GameTimer} */
        this._timerDelayBurstPop = null;

        /** @private @type {GameTimer} */
        this._timerDelayChameleonChange = null;

        /** @private @type {GameTimer} */
        this._timerDelayClearDisconnected = null;

        /** @private @type {GameTimer} */
        this._timerBlackPantherAbilityDelay = null;

        /** @private @type {GameTimer[]} */
        this._timerBlooming = [];

        /** @private @type {GameTimer} */
        this._timerOvertimeBubblesDelay = null;

        /** @private @type {GameTimer} */
        this._timerMultipleAbilityDelay = null;

        /** @private @type {GameTimer} */
        this._timerNextWaveDelay = null;

        /** @private @type {Number} */
        this._overtimeBubbles = 0;

        /** @private @type {Boolean} */
        this._overtimeBubblesSpawned = true;

        /** @private @type {Bubble[]} */
        this._bubblesToPop = [];

        /** @private @type {Number} */
        this._stateTimer = 0;
        /** @private @type {Boolean} */
        this._enemyReadyToAttack = false;

        /** @private @type {Boolean} */
        this._blackPantherAbilityActivated = false;

        /** @private @type {Boolean[]} */
        this._multiColorsMatched = [false, false, false, false, false, false];

        /** @private @type {Function} */
        this._generateNewLayoutCallback = null;

        /** @private @type {Boolean} */
        this._cheatCompleteLevel = false;
        /** @private @type {Boolean} */
        this._cheatFailLevel = false;

        Game.on(GameEvents.eCANNON_SHOOT, this._fireBubble, this);
        Game.on(GameEvents.eHEROES_DEAD, this._onHeroesDead, this);
        Game.on(GameEvents.eENEMIES_ATTACK_PHASE, this._onAttackPhaseCompleted, this);
        Game.on(GameEvents.eBUBBLE_COLORLESS_MATCHED, this._onColorlessMatched, this);
        Game.on(GameEvents.eBUBBLE_COUNTDOWN_EFFECT, this._onCountDownEffect, this);
        Game.on(GameEvents.eBUBBLE_SCATTER_DONE, this._onBubbleScatterDone, this);

        Game.on(GameEvents.eLIGHTNING_BUBBLES_CLEARED, this._onLightningBubblesCleared, this);

        Game.on(GameEvents.eGAME_ABILITY_ADD_BUBBLE, this._onAbilityAddBubble, this);
        Game.on(GameEvents.eGAME_ABILITY_ADD_RANDOM_LINE, this._onAbilityAddRandomLine, this);
        Game.on(GameEvents.eGAME_ABILITY_CHARIOT, this._onAbilityChariot, this);
        Game.on(GameEvents.eGAME_ABILITY_TRANSFORM_TO_SHIELD, this._onAbilityTransformToShield, this);
        Game.on(GameEvents.eGAME_ABILITY_HACKER, this._onAbilityHacker, this);
        Game.on(GameEvents.eGAME_ABILITY_ADD_OVERTIME, this._onAbilityAddOvertime, this);
        Game.on(GameEvents.eGAME_ABILITY_JAM, this._onAbilityStunHeroes, this);
        Game.on(GameEvents.eGAME_ABILITY_HEAVY_SLAM, this._onAbilityHeavySlam, this);
        Game.on(GameEvents.eGAME_ABILITY_PROXIMAS_SPEAR, this._onAbilityProximasSpear, this);
        Game.on(GameEvents.eGAME_ABILITY_RANDOM_SHOOTER, this._onAbilityRandomShooter, this);
        Game.on(GameEvents.eGAME_ABILITY_TRANSFORM_TO_STONE, this._onAbilityTransformToStone, this);
        Game.on(GameEvents.eGAME_ABILITY_SPACE_MANIPULATION, this._onAbilitySpaceManipulation, this);
        Game.on(GameEvents.eGAME_ABILITY_SNAP, this._onSnap, this);
        Game.on(GameEvents.eGAME_ABILITY_DOUBLE_EDGED_SWORD, this._onAbilityDoubleEdgedSword, this);

        Game.on(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ATTACK, this._onAbilityBlackPantherDelay, this);

        Game.on(GameEvents.eHEROES_ALL_OUT_ATTACK_CUTIN_DONE, this._onAllOutAttackCutInDone, this);
        Game.on(GameEvents.eGAME_START_WAVE_COMPLETED, this._onNewWavePopupDone, this);
        Game.on(GameEvents.eGAME_SPAWN_BLOOMING, this._onTriggerBlooming, this);
        Game.on(GameEvents.eRESET_BUTTON_INPUTS, this._onResetButtonInputs, this);

        Game.on(GameEvents.eBUBBLE_POPPED, this._onBubblePopped, this);

        Game.on(CheatEvents.eCOMPLETE_LEVEL, this._onCheatsCompleteLevel, this);
        Game.on(CheatEvents.eFAIL_LEVEL, this._onCheatsFailLevel, this);
        Game.on(CheatEvents.eSPAWN_BUBBLE_COLOR, this._onCheatsSpawnBubbleColor, this);
    }

    /**
     * @public
     * @param {number} levelId
     */
    createLevel(levelId) {
        this._levelId = levelId;

        this._shooter = new Shooter(this._bubbleFactory, this._scene);
        this._backgroundManager = new BackgroundManager(this._scene);
        this._particlesManager = new ParticlesManager(this._scene);
        this._blackPantherShockwave = new BlackPantherShockwave(this._scene);
        this._wallBounce = new WallBounce(this._scene);

        this._currentLevel = new Level();
        Common.currentLevel = this._currentLevel;
        this._currentLevel.load(levelId);

        this._bubbleFactory.setConstraints(this._currentLevel.getGenerationConstraints());
        this._bubbleFactory.generateInitialLayout(this._currentLevel.getInitialBubbles());
        this._enemies.setConstraints(this._currentLevel.getGenerationConstraints());

        this._backgroundManager.addBackgroundImage(this._currentLevel.getAct());

        this._bubblesToPop = [];
        this._cheatCompleteLevel = false;
        this._cheatFailLevel = false;
    }

    /**
     * The game context begins
     * @public
     */
    beginLevel() {
        this._setState(GameState.eGAME_START);

        Common.hudHeroes.beginLevel();

        this._heroes.beginLevel();
        this._enemies.beginLevel();
    }

    /**
     * @public
     */
    closeLevel() {
        // Hide the game scene
        this._scene.active = false;

        this._grid.clear();
        Common.collisions.clear();

        for (const bubble of this._removedBubbles) {
            bubble.destroy();
        }
        this._removedBubbles = [];

        this._heroes.closeLevel();
        this._enemies.closeLevel();

        this._lightning.clear();

        for (const ability of this._performedAbilities) {
            ability.destroy();
        }
        this._performedAbilities = [];

        if (this._currentLevel) {
            this._currentLevel.destroy();
            this._currentLevel = null;
            Common.currentLevel = null;
        }

        if (this._shooter) {
            this._shooter.destroy();
            this._shooter = null;
        }

        if (this._particlesManager) {
            this._particlesManager.destroy();
            this._particlesManager = null;
        }

        if (this._backgroundManager) {
            this._backgroundManager.destroy();
            this._backgroundManager = null;
        }

        if (this._blackPantherShockwave) {
            this._blackPantherShockwave.destroy();
            this._blackPantherShockwave = null;
        }

        if (this._wallBounce) {
            this._wallBounce.destroy();
            this._wallBounce = null;
        }

        if (this._timerDelayBurstPop) {
            this._timerDelayBurstPop.destroy();
            this._timerDelayBurstPop = null;
        }

        if (this._timerDelayChameleonChange) {
            this._timerDelayChameleonChange.destroy();
            this._timerDelayChameleonChange = null;
        }

        if (this._timerDelayClearDisconnected) {
            this._timerDelayClearDisconnected.destroy();
            this._timerDelayClearDisconnected = null;
        }

        if (this._timerBlackPantherAbilityDelay) {
            this._timerBlackPantherAbilityDelay.destroy();
            this._timerBlackPantherAbilityDelay = null;
        }

        if (this._tweenBubbleMovement) {
            this._tweenBubbleMovement.destroy();
            this._tweenBubbleMovement = null;
        }

        if (this._timerNextWaveDelay) {
            this._timerNextWaveDelay.destroy();
            this._timerNextWaveDelay = null;
        }

        if (this._timerOvertimeBubblesDelay) {
            this._timerOvertimeBubblesDelay.destroy();
            this._timerOvertimeBubblesDelay = null;
        }

        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        for (let timer of this._bloomingTimer) {
            if (timer) {
                timer.destroy();
            }
        }
        this._bloomingTimer = [];

        // Destroy bubbles not in the grid if any
        if (this._scene) {
            const bubbleLayer = this._scene.getLayer(SceneLayers.eBUBBLES);
            if (bubbleLayer && bubbleLayer.children) {
                for (const bubble of bubbleLayer.children) {
                    bubble.destroy();
                }
                bubbleLayer.removeChildren();
            }
        }
    }

    /**
     * Fires the current bubble in the cannon and calculate trajectory
     * @private
     * @param {Number} aimAngle - Aiming angle expressed in degrees
     * @param {Bubble} bubble
     * @param {Boolean} fromCannonOrhitSidewall
     */
    _fireBubble(aimAngle, bubble, fromCannonOrhitSidewall = false) {
        if (bubble.color < BubbleColor.eSPECIAL) {
            this._lastShotColor = bubble.color;
        }
        this._firedBubble = bubble;
        this._firedAngle = aimAngle;

        const aimRadians = (aimAngle * Math.PI) / 180;

        // Determine the entire bubble trajectory (excluding rebounds)
        let targetX = bubble.x + Math.cos(aimRadians) * FIRED_BUBBLE_COLLISION_LINE_LENGTH;
        let targetY = bubble.y + Math.sin(aimRadians) * FIRED_BUBBLE_COLLISION_LINE_LENGTH;

        const collisionRadius = Common.settings.collisionRadius;
        Common.collisions.bubbleHitTest(bubble.x, bubble.y, targetX, targetY, collisionRadius, this._collisionResult);

        if (this._collisionResult.hit) {
            targetX = this._collisionResult.contactX;
            targetY = this._collisionResult.contactY;
        }

        // Calculate trajectory time in milliseconds
        const bubbleSpeed = Common.settings.bubbleSpeed;
        const deltaX = targetX - this._firedBubble.x;
        const deltaY = targetY - this._firedBubble.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const movementTime = (1000 * distance) / bubbleSpeed;

        this._tweenBubbleMovement = Game.windows
            .getCurrentWindow()
            .createTween(this._firedBubble)
            .to({ x: targetX, y: targetY }, movementTime)
            .easing(GameTween.Easing.Linear)
            .onComplete(() => {
                this._onBubbleMovementCompleted();
            })
            .start();

        this._firedBubble.onFired(this._firedAngle, fromCannonOrhitSidewall);
        this._setState(GameState.eFIRING_BUBBLE);
    }

    /**
     * The fired bubble has reached its current destination
     * @private
     */
    _onBubbleMovementCompleted() {
        if (this._tweenBubbleMovement) this._tweenBubbleMovement.destroy();
        this._tweenBubbleMovement = null;

        // Rebound on top/bottom wall?
        if (this._collisionResult.hitSideWall) {
            Game.fire(GameEvents.eGAME_WALL_BOUNCE, { x: this._firedBubble.x, y: this._firedBubble.y, angle: this._firedAngle });
            this._fireBubble(-this._firedAngle, this._firedBubble, true);
            Game.audio.playSfx("IDSND_BUB_WALL_BOUNCE");
            return;
        }

        switch (this._firedBubble.color) {
            case BubbleColor.ePIERCE:
                this._onPierceBubbleCollision();
                break;
            case BubbleColor.eLIGHTNING:
                this._onLightningBubbleCollision();
                break;
            case BubbleColor.eBURST:
                this._onBurstBubbleCollision();
                break;
            default:
                this._snapFiredBubble();
        }

        this._onShotPhaseCompleted();
    }

    /**
     * The pierce bubble has collided with another bubble or back wall
     * @private
     */
    _onPierceBubbleCollision() {
        if (this._collisionResult.hitBubble) {
            this._specialBubbleHit = true;

            // Clear collided bubble
            const bubble = this._collisionResult.bubble;

            Common.collisions.removeBubble(bubble);
            this._grid.removeBubble(bubble);
            this._removedBubbles.push(bubble);

            bubble.pop(true, BubblePopSource.ePIERCE);

            this._createDamageTrail(bubble);

            this._bubbleManager.createDamageEvents([bubble], false, BubbleColor.eNONE);
            this._clearDisconnectedBubbles(this._firedBubble.getPos());

            --this._firedBubble.pierceCount;
            if (this._firedBubble.pierceCount > 0) {
                // Continue trajectory
                this._fireBubble(this._firedAngle, this._firedBubble);
            } else {
                // Pierce bubble is done
                this._firedBubble.pop(true, BubblePopSource.ePIERCE);
            }
        } else if (this._collisionResult.hitBackWall) {
            // Pierce bubble is done
            this._firedBubble.pop(true, BubblePopSource.ePIERCE);

            if (!this._specialBubbleHit) {
                this._onMatchMiss();
            }
        }
    }

    /**
     * The lightning bubble has collided with another bubble or back wall
     * @private
     */
    _onLightningBubbleCollision() {
        Game.audio.playSfx("IDSND_BUB_ABIL_ACTIV_LIGHTNING");
        // Only consider collisions with basic colored bubbles
        if (this._collisionResult.hitBubble && this._collisionResult.bubble.color < BubbleColor.eSPECIAL) {
            this._specialBubbleHit = true;

            this._firedBubble.pop();
            this._bubbleManager.createDamageEvents([this._firedBubble], false, BubbleColor.eLIGHTNING);
            this._beginLightning(this._collisionResult.bubble);
        } else if (this._collisionResult.hitBackWall) {
            // Chain lightning might occur after the snap
            this._snapLightningBubble();
        } else {
            // No effect when colliding with special color, causes a miss
            this._firedBubble.pop();
            this._onMatchMiss();
        }
    }

    /**
     * The lightning bubble hit the back wall ==> snap to closest grid position
     * @private
     */
    _snapLightningBubble() {
        this._grid.clipBubblePositionToGrid(this._firedBubble);

        const neighborBubbles = this._grid.getNeighborBubbles(this._firedBubble.column, this._firedBubble.row);
        if (neighborBubbles.length > 0) {
            // Choose randomly among the bubbles adjacent to the snapped lightning bubble
            const randomIndex = Math.floor(Math.random() * neighborBubbles.length);
            this._beginLightning(neighborBubbles[randomIndex]);
        } else {
            this._beginLightning(null);
        }

        this._firedBubble.pop();
    }

    /**
     * Clear all bubble of the color hit
     * @private
     * @param {Bubble} startBubble
     */
    _beginLightning(startBubble) {
        if (startBubble) {
            Common.particles.waitForParticles();
            this._lightning.begin(startBubble, this._grid.getBubblesOfColor(startBubble.color));
        }

        // Hit back wall
        if (!startBubble) {
            this._onMatchMiss();
        }
    }

    /**
     * Bubbles involved in the chain lightning effect have been cleared
     * @private
     * @param {Bubble[]} bubbles
     */
    _onLightningBubblesCleared(bubbles) {
        Common.collisions.removeBubbles(bubbles);
        this._grid.removeBubbles(bubbles);
        this._removedBubbles = this._removedBubbles.concat(bubbles);

        this._bubbleManager.createDamageEvents(bubbles, false, BubbleColor.eNONE);

        for (let i = 0; i < bubbles.length; ++i) {
            bubbles[i].pop();
            this._createDamageTrail(bubbles[i]);
        }

        const contactPosition = this._firedBubble.getPos();

        if (this._timerDelayClearDisconnected) {
            this._timerDelayClearDisconnected.destroy();
            this._timerDelayClearDisconnected = null;
        }

        this._timerDelayClearDisconnected = Game.windows.getCurrentWindow().createTimer(this._animationSettings.clearDisconnectedDelay);
        this._timerDelayClearDisconnected.onComplete = () => {
            this._clearDisconnectedBubbles(contactPosition);
        };
    }

    /**
     * The burst bubble has collided with another bubble or back wall
     * @private
     */
    _onBurstBubbleCollision() {
        if (this._collisionResult.hitBubble || this._collisionResult.hitBackWall) {
            this._specialBubbleHit = true;
            // Snap burst to grid
            this._grid.clipBubblePositionToGrid(this._firedBubble);

            // Explode burst bubble after delay
            if (this._timerDelayBurstPop) {
                this._timerDelayBurstPop.destroy();
                this._timerDelayBurstPop = null;
            }

            this._timerDelayBurstPop = Game.windows.getCurrentWindow().createTimer(TEMP_DELAY_POP_BURST_BUBBLE);
            this._timerDelayBurstPop.onComplete = () => {
                this._burstPop();
            };

            Game.audio.playSfx("IDSND_BUB_ABIL_ACTIV_HULK");
        }
    }

    /**
     * Explode burst bubble
     * @private
     */
    _burstPop() {
        if (this._timerDelayBurstPop) {
            this._timerDelayBurstPop.destroy();
            this._timerDelayBurstPop = null;
        }

        const heroData = Common.characters.getStats(Heroes.eHULK);
        const radius = Game.utils.assertNumber(heroData.BurstRadius, 0);
        let column = this._firedBubble.column;
        let row = this._firedBubble.row;
        const bubbleInRadius = [this._firedBubble];
        let offset = this._grid.isOffset;

        for (let i = 1; i <= radius; ++i) {
            // To Top
            row -= 1;
            if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));

            // To Top-right
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eBOTTOM_RIGHT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eBOTTOM_RIGHT];
                column += location.column;
                row += location.row;
                if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));
            }

            // To Bottom-right
            for (let j = 0; j < i; ++j) {
                row += 1;
                if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));
            }

            // To Bottom
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eBOTTOM_LEFT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eBOTTOM_LEFT];
                column += location.column;
                row += location.row;
                if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));
            }

            // To Bottom-left
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eTOP_LEFT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eTOP_LEFT];
                column += location.column;
                row += location.row;
                if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));
            }

            // To Top-left
            for (let j = 0; j < i; ++j) {
                row -= 1;
                if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));
            }

            // Back to top
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eTOP_RIGHT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eTOP_RIGHT];
                column += location.column;
                row += location.row;
                if (this._grid.getBubble(column, row) !== null) bubbleInRadius.push(this._grid.getBubble(column, row));
            }
        }

        // Pop bubbles
        for (const bubble of bubbleInRadius) {
            bubble.pop(true);
            this._createDamageTrail(bubble);
        }
        Common.collisions.removeBubbles(bubbleInRadius);
        this._grid.removeBubbles(bubbleInRadius);
        this._removedBubbles = this._removedBubbles.concat(bubbleInRadius);

        this._bubbleManager.createDamageEvents(bubbleInRadius, false, BubbleColor.eNONE);

        const contactPosition = this._firedBubble.getPos();

        if (this._timerDelayClearDisconnected) {
            this._timerDelayClearDisconnected.destroy();
            this._timerDelayClearDisconnected = null;
        }

        this._timerDelayClearDisconnected = Game.windows.getCurrentWindow().createTimer(this._animationSettings.clearDisconnectedDelay);
        this._timerDelayClearDisconnected.onComplete = () => {
            this._clearDisconnectedBubbles(contactPosition);
        };

        // Is this a hit or a miss?
        this._specialBubbleHit = bubbleInRadius.length > 1;
    }

    /**
     * @private
     * @param {Bubble} oldBubble
     * @param {BubbleColor} newColor
     */
    _onColorlessMatched(oldBubble, newColor) {
        oldBubble.changeColor({ color: newColor, special: BubbleColor.eUNDEFINED });
    }

    /**
     * @private
     * @param {Bubble} bubble
     */
    _onCountDownEffect(bubble) {
        if (bubble.specialType === BubbleColor.eBLOOMING || bubble.specialType === BubbleColor.eSTONE_BLOOMING) {
            bubble.triggerBlooming();
        }

        if (bubble.specialType === BubbleColor.eHEAL) {
            bubble.changeColor({ color: bubble.color, special: bubble.color }, true);
        }
    }

    /**
     * Find possible slots around the blooming bubble
     * @private
     * @param {Bubble} initialBubble
     */
    _onTriggerBlooming(initialBubble) {
        let column = initialBubble.column;
        let row = initialBubble.row;
        const radius = initialBubble.bloomingRadius;
        const nbrOfBubbles = initialBubble.bloomingNbr;
        const color = initialBubble.specialType === BubbleColor.eBLOOMING ? BubbleColor.eRANDOM_EDITOR : BubbleColor.eSTONE;
        let offset = this._grid.isOffset;
        const gridSlotsInRadius = [];

        for (let i = 1; i <= radius; ++i) {
            gridSlotsInRadius.push([]);

            // To Top
            row -= 1;
            const maxRow = this._grid.getNbRows(column);
            if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                gridSlotsInRadius[i - 1].push({ column: column, row: row });

            // To Top-right
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eBOTTOM_RIGHT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eBOTTOM_RIGHT];
                column += location.column;
                row += location.row;
                const maxRow = this._grid.getNbRows(column);
                if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                    gridSlotsInRadius[i - 1].push({ column: column, row: row });
            }

            // To Bottom-right
            for (let j = 0; j < i; ++j) {
                row += 1;
                const maxRow = this._grid.getNbRows(column);
                if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                    gridSlotsInRadius[i - 1].push({ column: column, row: row });
            }

            // To Bottom
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eBOTTOM_LEFT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eBOTTOM_LEFT];
                column += location.column;
                row += location.row;
                const maxRow = this._grid.getNbRows(column);
                if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                    gridSlotsInRadius[i - 1].push({ column: column, row: row });
            }

            // To Bottom-left
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eTOP_LEFT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eTOP_LEFT];
                column += location.column;
                row += location.row;
                const maxRow = this._grid.getNbRows(column);
                if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                    gridSlotsInRadius[i - 1].push({ column: column, row: row });
            }

            // To Top-left
            for (let j = 0; j < i; ++j) {
                row -= 1;
                const maxRow = this._grid.getNbRows(column);
                if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                    gridSlotsInRadius[i - 1].push({ column: column, row: row });
            }

            // Back to top
            for (let j = 0; j < i; ++j) {
                const location = offset
                    ? NEIGHBORS_OFFSETS[1 - (Math.abs(column) % 2)][nOffsetOrder.eTOP_RIGHT]
                    : NEIGHBORS_OFFSETS[Math.abs(column) % 2][nOffsetOrder.eTOP_RIGHT];
                column += location.column;
                row += location.row;
                const maxRow = this._grid.getNbRows(column);
                if (column >= 0 && column < NB_MAX_COLUMN && row >= 0 && row < maxRow)
                    gridSlotsInRadius[i - 1].push({ column: column, row: row });
            }
        }

        const possibleSlots = [];
        for (let i = radius - 1; i >= 0; --i) {
            const shuffledArray = shuffle(gridSlotsInRadius[i]);
            for (const slot of shuffledArray) {
                possibleSlots.push(slot);
            }
        }

        this._spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color);
    }

    /**
     * Spawn the required amount of bubbles around the blooming bubble
     * @param {Bubble} initialBubble
     * @param {Number} nbrOfBubbles
     * @param {{ column: Number, row: Number }[]} possibleSlots
     * @param {BubbleColor} color
     */
    _spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color) {
        const slot = possibleSlots.pop();
        if (nbrOfBubbles <= 0 || slot === undefined) {
            // Reset countdown label after bubble spawn
            let bloomingTimer = Game.windows.getCurrentWindow().createTimer(TEMP_BLOOMING_SPAWN_DELAY, { deleteOnCompletion: false });
            bloomingTimer.onComplete = () => {
                initialBubble.resetCountdown();
            };

            this._bloomingTimer.push(bloomingTimer);
            return;
        }

        const bubble = this._grid.getBubble(slot.column, slot.row);

        if (color === BubbleColor.eRANDOM_EDITOR) {
            if (bubble === null) {
                const bubble = this._bubbleFactory.createBubble(
                    this._grid.convertGridPosToScreenPos(slot.column, slot.row),
                    { color: BubbleColor.eRANDOM_EDITOR, special: BubbleColor.eRANDOM },
                    { column: slot.column, row: slot.row },
                    BubblePool.eGRID
                );
                if (bubble) bubble.bloomSpawn(initialBubble);
                --nbrOfBubbles;

                let bloomingTimer = Game.windows.getCurrentWindow().createTimer(TEMP_BLOOMING_SPAWN_DELAY, { deleteOnCompletion: false });
                bloomingTimer.onComplete = () => {
                    this._spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color);
                };
                this._bloomingTimer.push(bloomingTimer);
                return;
            } else {
                this._spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color);
            }
        }

        if (color === BubbleColor.eSTONE) {
            if (
                bubble !== null &&
                // Bubbles that will not be converted to stone
                bubble.color !== BubbleColor.eSTONE &&
                bubble.color !== BubbleColor.eSTONE_BLOOMING &&
                bubble.color !== BubbleColor.eBLOOMING &&
                bubble.specialType !== BubbleColor.eSHIELDED
            ) {
                bubble.changeColor(
                    {
                        color: BubbleColor.eSTONE,
                        special: BubbleColor.eSTONE
                    },
                    true
                );
                --nbrOfBubbles;

                let bloomingTimer = Game.windows.getCurrentWindow().createTimer(TEMP_BLOOMING_SPAWN_DELAY, { deleteOnCompletion: false });
                bloomingTimer.onComplete = () => {
                    this._spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color);
                };
                this._bloomingTimer.push(bloomingTimer);
                return;
            } else if (bubble === null) {
                const bubble = this._bubbleFactory.createBubble(
                    this._grid.convertGridPosToScreenPos(slot.column, slot.row),
                    { color: BubbleColor.eSTONE, special: BubbleColor.eSTONE },
                    { column: slot.column, row: slot.row },
                    BubblePool.eGRID
                );
                if (bubble) bubble.bloomSpawn(initialBubble);
                --nbrOfBubbles;

                let bloomingTimer = Game.windows.getCurrentWindow().createTimer(TEMP_BLOOMING_SPAWN_DELAY, { deleteOnCompletion: false });
                bloomingTimer.onComplete = () => {
                    this._spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color);
                };
                this._bloomingTimer.push(bloomingTimer);
                return;
            } else {
                this._spawnBlooming(initialBubble, nbrOfBubbles, possibleSlots, color);
            }
        }
    }

    /**
     * The fired bubble collided with another bubble of the back wall,
     * make it snap to the closest grid position
     * @private
     */
    _snapFiredBubble() {
        this._firedBubble.stopAnimations();
        this._grid.clipBubblePositionToGrid(this._firedBubble);

        this._grid.addBubble(this._firedBubble, this._firedBubble.column, this._firedBubble.row);
        Common.collisions.addBubble(this._firedBubble);
        this._lookForMatchingBubbles();
        Game.audio.playSfx("IDSND_BUB_STOP");
    }

    /**
     * After the fired bubble has snapped to the grid, look for color matches
     * @private
     */
    _lookForMatchingBubbles() {
        let match = false;

        const colorsToMatch = this._firedBubble.color === BubbleColor.eMULTICOLOR ? MULTICOLOR_VALUES : [this._firedBubble.color];

        const matchingClusters = [];
        const matchingColors = [];

        for (const colorToMatch of colorsToMatch) {
            const cluster = this._bubbleManager.findCluster(this._firedBubble, colorToMatch);
            if (cluster.length >= NB_COLOR_MATCH || (this._firedBubble.color === BubbleColor.eMULTICOLOR && cluster.length > 1)) {
                match = true;
                matchingClusters.push(cluster);
                matchingColors.push(colorToMatch);
            }
        }

        for (let i = 0; i < matchingClusters.length; ++i) {
            this._clearMatchingBubbles(matchingClusters[i], matchingColors[i], this._firedBubble);
        }

        // Apply direct impact effects on adjacent bubbles (shield and colorless bubbles)
        this._bubbleManager.applyImpactEffects(this._firedBubble);

        const contactPosition = this._firedBubble.getPos();

        if (this._timerDelayClearDisconnected) {
            this._timerDelayClearDisconnected.destroy();
            this._timerDelayClearDisconnected = null;
        }

        this._timerDelayClearDisconnected = Game.windows.getCurrentWindow().createTimer(this._animationSettings.clearDisconnectedDelay);
        this._timerDelayClearDisconnected.onComplete = () => {
            this._clearDisconnectedBubbles(contactPosition);
        };

        if (!match) {
            this._onMatchMiss();
        } else {
            if (this._firedBubble.color !== BubbleColor.ePIERCE) {
                // Tutorial trigger
                if (this._currentLevel.getLevelNumber() === LEVEL_ABILITIES) {
                    Common.tutorial.openTutorial(Tutorials.eLVL_2, TutorialStep.eMATCH_MADE, () => {
                        Game.fire(GameEvents.eSHOT_MATCH, this._firedBubble.color);

                        Common.tutorial.openTutorial(Tutorials.eLVL_2, TutorialStep.eABILITY_INFO);
                    });
                } else {
                    Game.fire(GameEvents.eSHOT_MATCH, this._firedBubble.color);
                }
            }
        }
    }

    /**
     * @private
     * @param {Bubble[]} bubbles
     * @param {BubbleColor} matchColor
     * @param {Bubble} firedBubble
     */
    _clearMatchingBubbles(bubbles, matchColor, firedBubble) {
        /** @type {Bubble[]} */
        let unshieldedBubbles = [];

        for (const bubble of bubbles) {
            let deltaX = bubble.x - firedBubble.x;
            let deltaY = bubble.y - firedBubble.y;
            let delay = Math.sqrt(deltaX * deltaX + deltaY * deltaY) * Common.settings.bubblePopTimeMultiplier;

            bubble.setPoppingDelay(delay);
            if (this._firedBubble.color === BubbleColor.eMULTICOLOR) {
                if (bubble.color < BubbleColor.eSPECIAL) this._multiColorsMatched[bubble.color] = true;
            }

            this._bubblesToPop.push(bubble);

            if (bubble.specialType !== BubbleColor.eSHIELDED) {
                unshieldedBubbles.push(bubble);
            }
        }

        // Remove matching bubble from grid immediately
        // We may want to clear disconnected bubbles before the animations complete
        Common.collisions.removeBubbles(unshieldedBubbles);
        this._grid.removeBubbles(unshieldedBubbles);
        this._removedBubbles = this._removedBubbles.concat(unshieldedBubbles);

        this._bubbleManager.createDamageEvents(unshieldedBubbles, true, matchColor);
    }

    /**
     * @private
     * @param {{x: Number, y: Number}} [contactPosition]
     */
    _clearDisconnectedBubbles(contactPosition) {
        const disconnectedBubbles = this._bubbleManager.findDisconnectedBubbles();
        if (disconnectedBubbles.length > 0) {
            Common.collisions.removeBubbles(disconnectedBubbles);
            this._grid.removeBubbles(disconnectedBubbles);
            this._removedBubbles = this._removedBubbles.concat(disconnectedBubbles);

            // Drop/scatter disconnected bubbles
            for (const bubble of disconnectedBubbles) {
                bubble.canCountdown = false;
                const requiresDamageTrail = this._requiresDamageTrail(bubble);
                bubble.drop(contactPosition, requiresDamageTrail, this._stateTimer);
            }

            this._bubbleManager.createDamageEvents(disconnectedBubbles, false, BubbleColor.eNONE);
        }
    }

    /**
     * The shot phase is completed, consider an enemy attack phase or reload the cannon
     * @private
     */
    _onShotPhaseCompleted() {
        // Pierce bubble hit/miss
        if (this._firedBubble.color === BubbleColor.eLIGHTNING || this._firedBubble.color === BubbleColor.eBURST) {
            if (this._specialBubbleHit) {
                Game.fire(GameEvents.eSHOT_MATCH, this._firedBubble.color);
            } else if (this._firedBubble.color !== BubbleColor.eLIGHTNING) {
                this._onMatchMiss();
            }
        }

        this._setState(GameState.eBUBBLE_CONTACT);
    }

    /**
     * Change the color of chameleon bubbles in the grid
     * @private
     */
    _updateChameleonBubbles() {
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this._grid.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                const bubble = this._grid.getBubble(column, row);
                if (bubble && bubble.specialType === BubbleColor.eCHAMELEON && bubble.color < BubbleColor.eSPECIAL) {
                    bubble.changeColor({
                        color: this._bubbleFactory.randomizeChameleonColor(bubble.color),
                        special: BubbleColor.eCHAMELEON
                    });
                }
            }
        }

        if (this._timerDelayChameleonChange) {
            this._timerDelayChameleonChange.destroy();
            this._timerDelayChameleonChange = null;
        }
    }

    /**
     * Tick down special bubbles countdown
     * @private
     */
    _tickDownCountdown() {
        Game.fire(GameEvents.eGAME_TICKDOWN_COUNTDOWN);
    }

    /**
     * @private
     */
    _onMatchMiss() {
        if (this._firedBubble.color === BubbleColor.eMULTICOLOR) {
            this._firedBubble.pop();
        }

        if (this._currentLevel.getLevelNumber() === 2) {
            Common.tutorial.openTutorial(Tutorials.eLVL_2, TutorialStep.eMATCH_MISSED, () => {
                this._enemies.onMatchMiss();
                this._enemyReadyToAttack = true;
            });
        } else {
            this._enemies.onMatchMiss();
            this._enemyReadyToAttack = true;
        }
    }

    /**
     * @private
     */
    _onAttackPhaseCompleted() {
        this._preemptiveStrike = false;
        if (this._state !== GameState.eVICTORY && this._state !== GameState.eDEFEAT) {
            this._setState(GameState.eBUBBLE_CHANGES);
        }
    }

    /**
     * @private
     */
    _onHeroesDead() {
        this._setState(GameState.eDEFEAT);
    }

    /**
     * @private
     * @param {GameState} state
     */
    _setState(state) {
        if (
            state === this._state ||
            ((this._state === GameState.eDEFEAT || this._state === GameState.eVICTORY) &&
                state !== GameState.eGAME_START &&
                state !== GameState.eDONE)
        ) {
            return;
        }

        this._state = state;

        switch (this._state) {
            case GameState.eGAME_START:
                this._initStateGameStart();
                break;
            case GameState.eSTART_WAVE:
                this._initStateStartWave();
                break;
            case GameState.ePLAYER_TURN:
                this._initStatePlayerTurn();
                break;
            case GameState.eFIRING_BUBBLE:
                this._initStateFiringBubble();
                break;
            case GameState.eBUBBLE_CONTACT:
                this._initStateBubbleContact();
                break;
            case GameState.eAPPLY_DAMAGE:
                this._initStateApplyDamage();
                break;
            case GameState.eENEMY_ATTACK:
                this._initStateEnemyAttack();
                break;
            case GameState.eBUBBLE_CHANGES:
                this._initStateBubbleChanges();
                break;
            case GameState.eBLACK_PANTHER_ATTACK:
                this._initStateBlackPantherAttack();
                break;
            case GameState.eVICTORY:
                this._initStateVictory();
                break;
            case GameState.eDEFEAT:
                this._initStateDefeat();
                break;
            case GameState.eDONE:
                this._initStateDone();
                break;
        }
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        this._enemies.updateLoop(deltaTime);

        this._heroes.updateLoop(deltaTime);

        this._shooter.updateLoop(deltaTime);

        if (this._cheatCompleteLevel) {
            this._cheatCompleteLevel = false;
            this._setState(GameState.eVICTORY);
        }

        if (this._cheatFailLevel) {
            this._cheatFailLevel = false;
            this._setState(GameState.eDEFEAT);
        }

        this._updateState(deltaTime);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateState(deltaTime) {
        switch (this._state) {
            case GameState.eGAME_START:
                this._updateStateGameStart(deltaTime);
                break;
            case GameState.eBUBBLE_CONTACT:
                this._updateStateBubbleContact(deltaTime);
                break;
            case GameState.eAPPLY_DAMAGE:
                this._updateStateApplyDamage(deltaTime);
                break;
            case GameState.eBUBBLE_CHANGES:
                this._updateStateBubbleChanges(deltaTime);
                break;
            case GameState.eBLACK_PANTHER_ATTACK:
                this._updateStateBlackPantherAttack(deltaTime);
                break;
            case GameState.eVICTORY:
                this._updateStateVictory(deltaTime);
                break;
            case GameState.eDEFEAT:
                this._updateStateDefeat(deltaTime);
                break;
        }
    }

    /**
     * @private
     */
    _initStateGameStart() {
        this._scene.active = true;
        this._levelWaves = this._currentLevel.getEnemies();
        this._currentWave = 0;
        this._overtimeBubbles = 0;
        this._preemptiveStrike = false;

        Game.fire(GameEvents.eHUD_HIDE_POPUPS);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateGameStart(deltaTime) {
        this._setState(GameState.eSTART_WAVE);
    }

    /**
     * A new wave begins
     * @private
     */
    _initStateStartWave() {
        this._enemies.endLastWave();

        if (this._currentWave < this._levelWaves.length) {
            // Delay to allow the death animations to complete before staring the new wave
            const delay = this._currentWave > 0 ? Common.settings.enemyDeathSlideTime : 0;

            this._timerNextWaveDelay = Game.windows.getCurrentWindow().createTimer(delay);
            this._timerNextWaveDelay.onComplete = () => {
                this._enemies.startNewWave(this._currentWave, this._levelWaves);
                Game.fire(GameEvents.eGAME_START_WAVE, this._currentWave + 1, this._levelWaves.length);
            };
        } else {
            this._setState(GameState.eVICTORY);
        }

        Game.fire(GameEvents.eGAME_DISABLE_INPUTS);
    }

    /**
     * The sliding popup announcing the new wave is done
     * @private
     */
    _onNewWavePopupDone() {
        Game.fire(GameEvents.eHUD_HIDE_POPUPS);

        // Consider the all-out attack (last enemy of previous wave killed clearing the board)
        // We must consider the all-out before a possible pre-emptive strike
        this._considerGenerateNewLayout(this._endNewWave.bind(this));
    }

    /**
     * The sliding popup announcing the new wave is done
     * @private
     */
    _endNewWave() {
        if (this._enemies.haveFirstStrike()) {
            this._preemptiveStrike = true;
            this._setState(GameState.eENEMY_ATTACK);
        } else {
            this._setState(GameState.ePLAYER_TURN);
        }
    }

    /**
     * The player's turn begins
     * @private
     */
    _initStatePlayerTurn() {
        for (const timer of this._bloomingTimer) {
            timer.destroy();
        }
        this._bloomingTimer = [];

        this._removedBubbles = [];

        // Consider the all-out attack
        this._considerGenerateNewLayout(this._continuePlayerTurn.bind(this));
    }

    /**
     * The player's turn continues after considering the all-out attack
     * @private
     */
    _continuePlayerTurn() {
        this._particlesManager.beginTurn();
        this._blackPantherAbilityActivated = false;
        this._bubbleContactStateTime = Common.settings.maxTimeToPopAllBubbles;
        this._multiColorsMatched = [false, false, false, false, false, false];

        // Tick down heroes ability duration
        Game.fire(GameEvents.eHEROES_ABILITY_DURATION_TICK);

        if (!this._grid.isGridEmpty()) {
            // Reload cannon
            Common.shooter.setAvailableColors(this._grid.getAllGridColors());
            Game.fire(GameEvents.eCANNON_RELOAD);
            Game.fire(GameEvents.eGAME_ENABLE_INPUTS);
        }

        // Tutorial triggers
        const levelNumber = this._currentLevel.getLevelNumber();

        // Level 1 - Base mechanics
        if (levelNumber === FIRST_LEVEL) {
            Common.tutorial.openTutorial(Tutorials.eLVL_1, TutorialStep.eRELEASE_TO_SHOOT);
        }

        // Level 2 - Abilities
        if (levelNumber === LEVEL_ABILITIES) {
            for (let i = 0; i < TEAM_SIZE; ++i) {
                if (this._heroes.getHero(i).canUseAbility()) {
                    Common.tutorial.openTutorial(Tutorials.eLVL_2, TutorialStep.eABILITY_READY);
                    break;
                }
            }
        }

        // Special bubbles
        // Will not actually open if already completed
        if (levelNumber > LEVEL_ABILITIES) {
            if (this._grid.anyBubbleOfSpecialType(BubbleColor.eHEAL)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_HEAL, TutorialStep.eFIRST);
            }
            if (this._grid.anyBubbleOfSpecialType(BubbleColor.eCHAMELEON)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_CHAMELEON, TutorialStep.eFIRST);
            }
            if (this._grid.anyBubbleOfSpecialType(BubbleColor.eSHIELDED)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_HIDDEN, TutorialStep.eFIRST);
            }
            if (this._grid.anyBubbleOfColor(BubbleColor.eSTONE)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_STONE, TutorialStep.eFIRST);
            }
            if (this._grid.anyBubbleOfColor(BubbleColor.eCOLORLESS)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_COLORLESS, TutorialStep.eFIRST);
            }
            if (this._grid.anyBubbleOfColor(BubbleColor.eBLOOMING)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_BLOOMING, TutorialStep.eFIRST);
            }
            if (this._grid.anyBubbleOfColor(BubbleColor.eSTONE_BLOOMING)) {
                Common.tutorial.openTutorial(Tutorials.eBUBBLE_BLOOMING_STONE, TutorialStep.eFIRST);
            }
        }

        Common.tutorial.beginTurn();
    }

    /**
     * Check if the grid is empty and trigger the all-out attack
     * @private
     * @param {Function} callback
     */
    _considerGenerateNewLayout(callback) {
        this._generateNewLayoutCallback = callback;

        const triggerAllOut = this._grid.isGridEmpty() && this._currentLevel;
        if (triggerAllOut) {
            Game.fire(GameEvents.eGAME_DISABLE_INPUTS);
            Game.fire(GameEvents.eHEROES_ALL_OUT_ATTACK);
        } else {
            this._generateNewLayoutCallback();
        }
    }

    /**
     * Generate a new layout if the grid is empty after the all-out attack
     * @private
     */
    _onAllOutAttackCutInDone() {
        Common.tutorial.openTutorial(Tutorials.eALL_OUT_ATTACK, TutorialStep.eFIRST);

        Game.fire(GameEvents.eGAME_DISABLE_INPUTS);

        const randomGridIndex = Math.floor(Math.random() * this._currentLevel.getNbRespawnGrids());
        this._bubbleFactory.generateRespawnLayout(
            this._currentLevel.getRespawnBubbles(randomGridIndex),
            this._respawnLayoutGenerationCompleted.bind(this)
        );
    }

    /**
     * @private
     */
    _respawnLayoutGenerationCompleted() {
        Common.shooter.setAvailableColors(this._grid.getAllGridColors());
        Common.shooter.fillReserveAfterReload();

        this._generateNewLayoutCallback();
    }

    /**
     * End of generation of the new layout, going back to the player turn.
     * @private
     */
    _onResetButtonInputs() {
        Game.fire(GameEvents.eGAME_DISABLE_INPUTS);

        if (this._state === GameState.ePLAYER_TURN) {
            Game.fire(GameEvents.eGAME_ENABLE_INPUTS);
        }
    }

    /**
     * @private
     */
    _initStateFiringBubble() {
        this._stateTimer = 0;
        Game.fire(GameEvents.eGAME_DISABLE_INPUTS);
    }

    /**
     * @private
     */
    _initStateBubbleContact() {
        this._stateTimer = 0;
    }

    /**
     * @private
     */
    _initStateEnemyAttack() {
        this._enemies.attack(this._preemptiveStrike);
    }

    /**
     * Apply changes to the bubbles in the grid
     *  - Add bubble abilities
     *  - Chameleon bubbles
     *  - Tick down on bubbles with counters
     * @private
     */
    _initStateBubbleChanges() {
        this._stateTimer = 0;
        // Stops bubble effects when all enemies are dead at the last wave.
        if (!this._enemies.areStillAlive() && this._currentWave === this._levelWaves.length - 1) return;

        this._overtimeBubblesSpawned = true;

        if (!this._grid.isGridEmpty()) {
            if (this._overtimeBubbles > 0) {
                this._overtimeBubblesSpawned = false;

                let addBubble = {
                    nbBubbles: this._overtimeBubbles,
                    color: BubbleColor.eRANDOM,
                    startPos: { x: SHOOTER_X_POSITION, y: SHOOTER_Y_POSITION },
                    dodgeFirst: true,
                    callback: null
                };

                this._onAbilityAddBubble(addBubble);
                --this._overtimeBubbles;
            }

            // Take into consideration the particles travel time and spawn duration
            const delay = Common.settings.animations.spawnDuration + ENEMY_ABILITY_PARTICLE_TRAVEL_TIME;
            this._timerOvertimeBubblesDelay = Game.windows.getCurrentWindow().createTimer(delay);
            this._timerOvertimeBubblesDelay.onComplete = () => {
                this._overtimeBubblesSpawned = true;
            };
        }

        Game.fire(GameEvents.eGAME_SET_BUFF_TIMER, BuffDebuff.eOVERTIME, this._overtimeBubbles);

        // Update Chameleon bubbles
        if (this._timerDelayChameleonChange) {
            this._timerDelayChameleonChange.destroy();
            this._timerDelayChameleonChange = null;
        }

        if (this._grid.anyBubbleOfSpecialType(BubbleColor.eCHAMELEON)) {
            this._timerDelayChameleonChange = Game.windows
                .getCurrentWindow()
                .createTimer(TEMP_DELAY_CHANGE_CHAMELEON_COLOR, { deleteOnCompletion: false });
            this._timerDelayChameleonChange.onComplete = () => {
                this._updateChameleonBubbles();
            };
        }

        this._tickDownCountdown();
        this._specialBubbleHit = false;
    }

    /**
     * @private
     */
    _initStateVictory() {
        this._stateTimer = 0;
        Common.tutorial.saveProgress();
        Game.fire(GameEvents.eGAME_WIN_LEVEL, this._levelId >= LAST_LEVEL);

        if (this._levelId >= Common.save.getProgression() && this._levelId < LAST_LEVEL) {
            Game.fire(GameEvents.eLEVEL_MANAGER_GAME_PROGRESS, this._levelId + 1);
        }
    }

    /**
     * @private
     */
    _initStateDefeat() {
        this._stateTimer = 0;
        Game.fire(GameEvents.eGAME_LOSE_LEVEL);
    }

    /**
     * @private
     */
    _initStateDone() {
        Game.audio.stopAllSfx();
        Game.windows.open("WindowResults");
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateBubbleContact(deltaTime) {
        if (Game.isPaused()) {
            return;
        }

        this._stateTimer += deltaTime;

        if (this._timerDelayBurstPop) return;

        this._grid.considerDamageZone();

        let nbBubblesToPop = 0;

        for (let i = 0; i < this._bubblesToPop.length; ++i) {
            if (this._bubblesToPop[i] && !this._bubblesToPop[i].isPopping && this._stateTimer >= this._bubblesToPop[i].poppingDelay) {
                if (this._bubblesToPop[i].specialType !== BubbleColor.eSHIELDED) {
                    this._createDamageTrail(this._bubblesToPop[i]);
                }
                this._bubblesToPop[i].pop();
            } else if (this._bubblesToPop[i] && this._bubblesToPop[i].isPopping) {
                ++nbBubblesToPop;
            }
        }

        if (nbBubblesToPop >= this._bubblesToPop.length) {
            this._bubbleContactStateTime = 0;
        }

        if (this._stateTimer < this._bubbleContactStateTime) return;

        // Pop any bubbles that went over the max time.
        for (let i = 0; i < this._bubblesToPop.length; ++i) {
            const bubbleToPop = this._bubblesToPop[i];
            if (bubbleToPop && !bubbleToPop.isPopping) {
                Common.collisions.removeBubble(bubbleToPop);
                this._grid.removeBubble(bubbleToPop);
                this._removedBubbles.push(bubbleToPop);

                bubbleToPop.pop();
                this._createDamageTrail(bubbleToPop);
            }
        }

        this._bubblesToPop = [];

        this._particlesManager.updateLoop(deltaTime);

        const particlesDone = this._particlesManager.verifyCompletion();
        if (!particlesDone) {
            return;
        }

        if (this._enemyReadyToAttack && this._enemies.areStillAlive()) {
            this._enemyReadyToAttack = false;
            this._setState(GameState.eENEMY_ATTACK);
        } else {
            this._setState(GameState.eAPPLY_DAMAGE);
        }
    }

    /**
     * @private
     */
    _initStateApplyDamage() {
        this._stateTimer = 0;

        this._enemies.applyDamageToEnemies();
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateApplyDamage(deltaTime) {
        this._stateTimer += deltaTime;

        if (this._stateTimer < Common.settings.enemyShakeDuration) {
            return;
        }

        if (this._blackPantherAbilityActivated) {
            if (this._enemies.areStillAlive()) {
                this._setState(GameState.ePLAYER_TURN);
            } else {
                ++this._currentWave;
                this._setState(GameState.eSTART_WAVE);
            }
        } else {
            this._setState(GameState.eBUBBLE_CHANGES);
        }
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateBubbleChanges(deltaTime) {
        // Wait until chameleon animation is completed
        if (this._timerDelayChameleonChange) {
            return;
        }

        let bloomingDone = true;
        for (const timer of this._bloomingTimer) {
            if (timer.isPlaying()) {
                bloomingDone = false;
                break;
            }
        }

        if (!bloomingDone) {
            return;
        }

        // Wait for added overtime bubbles spawn completion
        if (!this._overtimeBubblesSpawned) {
            return;
        }

        this._grid.considerDamageZone();

        // Black Panther ability?
        this._blackPantherAbilityActivated = this._heroes.checkBlackPantherAbility();
        if (this._blackPantherAbilityActivated) {
            this._setState(GameState.eBLACK_PANTHER_ATTACK);
        } else {
            const enemiesAlive = this._enemies.areStillAlive();
            if (enemiesAlive) {
                this._setState(GameState.ePLAYER_TURN);
            } else {
                ++this._currentWave;
                this._setState(GameState.eSTART_WAVE);
            }
        }
    }

    /**
     * @private
     */
    _initStateBlackPantherAttack() {
        this._stateTimer = 0;

        this._heroes.activateBlackPantherAbility();

        // Make sure the damage particles are completed
        Common.particles.beginTurn();
        const targetBubbles = Common.grid.getBubblesForBlackPanther();
        if (targetBubbles.length > 0) {
            Common.particles.waitForParticles();
        }
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateBlackPantherAttack(deltaTime) {
        this._stateTimer += deltaTime;

        // Await damage particles completion
        this._particlesManager.updateLoop(deltaTime);
        const particlesDone = this._particlesManager.verifyCompletion();
        if (!particlesDone) {
            return;
        }

        this._setState(GameState.eAPPLY_DAMAGE);
    }

    /**
     * Delay before starting Black Panther's attack so the shockwave can travel a bit.
     * @private
     * @param {Boolean} stillActive
     * @param {Number} nbBubbles
     */
    _onAbilityBlackPantherDelay(stillActive, nbBubbles) {
        if (this._timerBlackPantherAbilityDelay) {
            this._timerBlackPantherAbilityDelay.destroy();
            this._timerBlackPantherAbilityDelay = null;
        }

        this._timerBlackPantherAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.blackPantherShockwavePopTime);
        this._timerBlackPantherAbilityDelay.onComplete = () => {
            this._onAbilityBlackPantherShockwavePop(nbBubbles);
        };
    }

    /**
     * The shockwave animation is at the desired time rario to pop bubbles
     * Pop the specified amount of bubbles randomly, but starting with harmful special types
     * @private
     * @param {Number} nbBubbles
     */
    _onAbilityBlackPantherShockwavePop(nbBubbles) {
        /** @type {Bubble[][]} */
        const bubblePool = this._grid.getBubblesForBlackPanther();

        // Choose bubbles
        /** @type {Bubble[]} */
        const bubbles = [];
        let pool = 0;
        while (nbBubbles > 0) {
            const length = bubblePool[pool].length;
            if (length === 0) {
                if (pool < bubblePool.length - 1) {
                    ++pool;
                    continue;
                } else {
                    break;
                }
            }
            const random = Math.floor(Math.random() * length);
            bubbles.push(bubblePool[pool].splice(random)[0]);
            --nbBubbles;
        }

        // Pop bubbles
        for (const bubble of bubbles) {
            bubble.pop(true);
            this._createDamageTrail(bubble);
        }
        Common.collisions.removeBubbles(bubbles);
        this._grid.removeBubbles(bubbles);
        this._removedBubbles = this._removedBubbles.concat(bubbles);

        this._bubbleManager.createDamageEvents(bubbles, false, BubbleColor.eNONE);

        if (this._timerDelayClearDisconnected) {
            this._timerDelayClearDisconnected.destroy();
            this._timerDelayClearDisconnected = null;
        }

        this._timerDelayClearDisconnected = Game.windows.getCurrentWindow().createTimer(this._animationSettings.clearDisconnectedDelay);
        this._timerDelayClearDisconnected.onComplete = () => {
            this._clearDisconnectedBubbles(null);
        };
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateVictory(deltaTime) {
        this._stateTimer += deltaTime;

        if (this._stateTimer >= Common.settings.victoryAnimationDuration) {
            this._setState(GameState.eDONE);
        }
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateDefeat(deltaTime) {
        this._stateTimer += deltaTime;

        if (this._stateTimer >= Common.settings.victoryAnimationDuration) {
            this._setState(GameState.eDONE);
        }
    }

    /**
     * Adds nbBubbles bubbles of the specified type to the grid
     * Bubbles should be connected to other bubbles or on the first column
     * @private
     * @param {Object} addBubbleData
     */
    _onAbilityAddBubble(addBubbleData) {
        Game.audio.playSfx("IDSND_BUB_ADD_RANDOM");
        const ability = new AbilityAddBubble(
            addBubbleData.nbBubbles,
            addBubbleData.color,
            this._lastShotColor,
            addBubbleData.startPos,
            addBubbleData.dodgeFirst,
            addBubbleData.callback
        );
        this._performedAbilities.push(ability);
    }

    /**
     * Adds nbOvertimeBubbles bubbles of the specified type to the grid
     * Bubbles should be connected to other bubbles or on the first column
     * @private
     * @param {Number} nbOvertimeBubbles
     * @param {Function} callback
     */
    _onAbilityAddOvertime(nbOvertimeBubbles, callback) {
        this._overtimeBubbles += nbOvertimeBubbles;
        Game.fire(GameEvents.eGAME_SET_NUMBER_OVERTIME_TURNS, this._overtimeBubbles);
        Game.audio.playSfx("IDSND_ADD_OVERTIME");

        if (callback) {
            callback();
        }
    }

    /**
     * Stuns nbHeroes heroes for nbTurns turns.
     * @private
     * @param {Number} nbHeroes
     * @param {Number} nbTurns
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityStunHeroes(nbHeroes, nbTurns, startPos, callback) {
        const ability = new AbilityStun(nbHeroes, nbTurns, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * Adds nbBubbles bubbles of the specified type to the grid
     * Bubbles should be connected to other bubbles or on the first column
     * @private
     * @param {Object} options
     * @param {Number} options.attackDamage
     * @param {Number} options.nbBubbles
     * @param {BubbleColor} options.bubbleColor
     * @param {{x: Number, y: Number}} options.startPos
     * @param {Function} options.callback
     */
    _onAbilityHeavySlam(options) {
        const ability1 = new AbilityAttack(options.attackDamage, options.startPos, null);
        this._performedAbilities.push(ability1);

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            const ability2 = new AbilityAddBubble(
                options.nbBubbles,
                options.bubbleColor,
                this._lastShotColor,
                options.startPos,
                false,
                options.callback
            );
            this._performedAbilities.push(ability2);
        };
    }

    /**
     * Stuns some Avengers and also damages them.
     * @private
     * @param {Object} options
     * @param {Number} options.attackDamage
     * @param {Number} options.nbHeroes
     * @param {Number} options.nbTurns
     * @param {{x: Number, y:Number}} options.startPos
     * @param {Function} options.callback
     */
    _onAbilityProximasSpear(options) {
        const ability1 = new AbilityAttack(options.attackDamage, options.startPos, null);
        this._performedAbilities.push(ability1);
        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            const ability2 = new AbilityStun(options.nbHeroes, options.nbTurns, options.startPos, options.callback);
            this._performedAbilities.push(ability2);
        };
    }

    /**
     * @private
     * @param {Number} nbBubbles
     * @param {{x: Number, y:Number}} startPos
     * @param {Function} callback
     */
    _onAbilityRandomShooter(nbBubbles, startPos, callback) {
        const ability = new AbilityRandomShooter(nbBubbles, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * Adds a new lines of bubble top the right of the grid
     * @private
     * @param {Number} nbLines
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityAddRandomLine(nbLines, startPos, callback) {
        const ability = new AbilityAddLine(nbLines, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * Transforms nbBubbles bubbles into chameleon bubbles
     * @private
     * @param {Number} nbBubbles
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityChariot(nbBubbles, startPos, callback) {
        const ability = new AbilityChariot(nbBubbles, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * Transforms nbBubbles bubbles into stone bubbles
     * @private
     * @param {Number} nbBubbles
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityTransformToStone(nbBubbles, startPos, callback) {
        const ability = new AbilityTransformToStone(nbBubbles, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * Randomizes all bubbles in the grid
     * @private
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilitySpaceManipulation(startPos, callback) {
        let coloredBubbles = this._grid.getAllGridBubbles();

        const randomSelector = new RandomSelector();
        for (let i = 0; i < coloredBubbles.length; ++i) {
            const randomBasicColor = Math.floor(Math.random() * BubbleColor.eSPECIAL);

            randomSelector.reset();

            randomSelector.addChoice(
                { color: randomBasicColor, special: BubbleColor.eNONE },
                Common.settings.spaceManipulation.probRegular
            );

            randomSelector.addChoice({ color: randomBasicColor, special: BubbleColor.eHEAL }, Common.settings.spaceManipulation.probHeal);

            randomSelector.addChoice(
                { color: randomBasicColor, special: BubbleColor.eSHIELDED },
                Common.settings.spaceManipulation.probShield
            );

            randomSelector.addChoice(
                { color: randomBasicColor, special: BubbleColor.eCHAMELEON },
                Common.settings.spaceManipulation.probChameleon
            );

            randomSelector.addChoice(
                { color: BubbleColor.eCOLORLESS, special: BubbleColor.eCOLORLESS },
                Common.settings.spaceManipulation.probColorless
            );

            randomSelector.addChoice(
                { color: BubbleColor.eSTONE, special: BubbleColor.eNONE },
                Common.settings.spaceManipulation.probStone
            );

            randomSelector.addChoice(
                { color: BubbleColor.eBLOOMING, special: BubbleColor.eBLOOMING },
                Common.settings.spaceManipulation.probBlooming
            );

            randomSelector.addChoice(
                { color: BubbleColor.eSTONE_BLOOMING, special: BubbleColor.eSTONE_BLOOMING },
                Common.settings.spaceManipulation.probStoneBlooming
            );

            coloredBubbles[i].changeColor(randomSelector.choose(), false);
        }

        if (callback) {
            callback();
        }
    }

    /**
     * THANOS SNAP!
     * @private
     * @param {Number} nbHeroes
     * @param {Number} nbTurns
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onSnap(nbHeroes, nbTurns, startPos, callback) {
        const ability1 = new AbilityAttack(this._heroes.getTotalHP() / 2, startPos, null);
        this._performedAbilities.push(ability1);

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            const ability2 = new AbilityStun(nbHeroes, nbTurns, startPos, callback);
            this._performedAbilities.push(ability2);
        };
    }

    /**
     * Thanos' Double-Edged Sword ability
     * @private
     * @param {Number} attackDamage
     * @param {Number} nbLinesToAdd
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityDoubleEdgedSword(attackDamage, nbLinesToAdd, startPos, callback) {
        const ability1 = new AbilityAttack(attackDamage, startPos, null);
        this._performedAbilities.push(ability1);

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            const ability2 = new AbilityAddLine(nbLinesToAdd, startPos, callback);
            this._performedAbilities.push(ability2);
        };
    }

    /**
     * Transforms nbBubbles bubbles into other colored bubbles
     * @private
     * @param {Number} nbBubbles
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityHacker(nbBubbles, startPos, callback) {
        const ability = new AbilityHacker(nbBubbles, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * Transforms nbBubbles bubbles into hidden/shielded bubbles
     * @private
     * @param {Number} nbBubbles
     * @param {{x: Number, y: Number}} startPos
     * @param {Function} callback
     */
    _onAbilityTransformToShield(nbBubbles, startPos, callback) {
        const ability = new AbilityTransformToShield(nbBubbles, startPos, callback);
        this._performedAbilities.push(ability);
    }

    /**
     * @private
     * @param {Bubble} bubble
     */
    _onBubblePopped(bubble) {
        Common.collisions.removeBubble(bubble);
        this._grid.removeBubble(bubble);
        bubble.destroy();
    }

    /**
     * @private
     * @param {Bubble} bubble
     */
    _onBubbleScatterDone(bubble) {
        if (this._particlesManager) {
            this._particlesManager.createDamageParticle(bubble.getPos(), bubble.color);
        }
    }

    /**
     * Does the clearance of this bubble required a damage trail?
     * @private
     * @param {Bubble} bubble
     * @returns {Boolean}
     */
    _requiresDamageTrail(bubble) {
        let requiresDamageTrail = false;
        if (bubble.color < BubbleColor.eSPECIAL) {
            requiresDamageTrail = true;
        }
        if (bubble.color === BubbleColor.eSTONE && this._enemies.currentEnemiesWeaknesses[BubbleColor.eSTONE] === true) {
            requiresDamageTrail = true;
        }

        return requiresDamageTrail;
    }

    /**
     * @private
     * @param {Bubble} bubble
     * @returns {Boolean}
     */
    _createDamageTrail(bubble) {
        let createdDamageTrail = false;
        if (bubble.color < BubbleColor.eSPECIAL) {
            this._particlesManager.createDamageParticle(bubble.getPos(), bubble.color);
            createdDamageTrail = true;
        } else if (bubble.color === BubbleColor.eMULTICOLOR) {
            for (let i = 0; i < this._multiColorsMatched.length; ++i) {
                if (this._multiColorsMatched[i]) {
                    this._particlesManager.createDamageParticle(bubble.getPos(), i);
                    createdDamageTrail = true;
                }
            }
        }

        return createdDamageTrail;
    }

    /**
     * @public
     * @returns {Number}
     */
    getAct() {
        return this._currentLevel.getAct();
    }

    /**
     * @private
     */
    _onCheatsCompleteLevel() {
        this._cheatCompleteLevel = true;
    }

    /**
     * @private
     */
    _onCheatsFailLevel() {
        this._cheatFailLevel = true;
    }

    /**
     * Spawn 5 bubble of the specified type in the grid
     * @private
     * @param {String} color
     */
    _onCheatsSpawnBubbleColor(color) {
        let addBubble = {
            nbBubbles: 5,
            color: BUBBLE_COLOR_NAME_TO_ID[color],
            startPos: null,
            dodgeFirst: false,
            callback: null
        };
        this._onAbilityAddBubble(addBubble);
    }
}
